import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Home, Clock, FileText, LogOut, Menu, X, MessageSquare, Timer, Calendar, MoreHorizontal, Wallet } from 'lucide-react';
import SubscriptionGuard from '../components/SubscriptionGuard';

const WorkerLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/worker/login'); };

  // Primära items i bottom-nav (max 5 för läsbarhet på liten skärm)
  const primaryItems = [
    { path: '/worker', label: 'Idag', icon: Home },
    { path: '/worker/schedule', label: 'Schema', icon: Calendar },
    { path: '/worker/report-time', label: 'Rapport', icon: FileText },
    { path: '/worker/personal-time', label: 'Tid', icon: Timer },
  ];
  // Sekundära items i "Mer"-menyn
  const secondaryItems = [
    { path: '/worker/chat', label: 'Chatt', icon: MessageSquare },
    { path: '/worker/history', label: 'Historik', icon: Clock },
    { path: '/worker/payslips', label: 'Lönespec', icon: Wallet },
  ];
  const allItems = [...primaryItems, ...secondaryItems];

  return (
    <SubscriptionGuard>
    <div className="min-h-screen bg-background flex flex-col" data-testid="worker-layout">
      {/* Mobile Header — respekterar iOS notch */}
      <header
        className="bg-card border-b border-border px-4 h-14 flex items-center justify-between sticky top-0 z-40"
        style={{ paddingTop: 'env(safe-area-inset-top)', height: 'calc(56px + env(safe-area-inset-top))' }}
      >
        <div className="flex items-center gap-2">
          <img src="/operio-logo-2.png" alt="Operio" className="w-7 h-7" />
          <span className="text-lg font-bold text-foreground" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Operio</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground max-w-[120px] truncate">{user?.name || user?.email}</span>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="w-10 h-10 flex items-center justify-center text-muted-foreground rounded-full hover:bg-muted"
            data-testid="worker-menu-btn"
            aria-label={menuOpen ? "Stäng meny" : "Öppna meny"}
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      {menuOpen && (
        <div className="bg-card border-b border-border px-4 py-2 space-y-1" data-testid="worker-drawer">
          {allItems.map(item => {
            const Icon = item.icon;
            const isActive = item.path === '/worker' ? location.pathname === '/worker' : location.pathname.startsWith(item.path);
            return (
              <Link key={item.path} to={item.path} onClick={() => setMenuOpen(false)}
                className={`flex items-center gap-3 px-3 py-3 rounded text-sm ${isActive ? 'bg-[hsl(16,100%,55%)]/10 text-[hsl(16,100%,55%)] font-medium' : 'text-muted-foreground'}`}>
                <Icon size={18} /> {item.label}
              </Link>
            );
          })}
          <button onClick={handleLogout} className="flex items-center gap-3 px-3 py-3 rounded text-sm text-destructive w-full" data-testid="worker-logout-btn">
            <LogOut size={18} /> Logga ut
          </button>
        </div>
      )}

      <main className="flex-1 p-4 max-w-lg w-full mx-auto pb-24 native-scroll">
        <Outlet />
      </main>

      {/* Bottom Navigation — safe-area för Android nav-bar och iOS home-indicator */}
      <nav
        className="fixed bottom-0 left-0 right-0 bg-card border-t border-border flex items-center justify-around py-2 z-40 mobile-bottom-nav"
        data-testid="worker-bottom-nav"
      >
        {primaryItems.map(item => {
          const Icon = item.icon;
          const isActive = item.path === '/worker' ? location.pathname === '/worker' : location.pathname.startsWith(item.path);
          return (
            <Link
              key={item.path} to={item.path}
              data-testid={`worker-nav-${item.path.split('/').pop() || 'today'}`}
              className={`flex flex-col items-center justify-center gap-0.5 flex-1 min-w-0 py-1.5 min-h-[44px] ${isActive ? 'text-[hsl(16,100%,55%)]' : 'text-muted-foreground'}`}
            >
              <Icon size={20} />
              <span className="text-[10px] font-medium truncate">{item.label}</span>
            </Link>
          );
        })}
        {/* "Mer"-knapp öppnar drawer så alla items når fram */}
        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          className="flex flex-col items-center justify-center gap-0.5 flex-1 py-1.5 min-h-[44px] text-muted-foreground"
          data-testid="worker-nav-more"
          aria-label="Fler alternativ"
        >
          <MoreHorizontal size={20} />
          <span className="text-[10px] font-medium">Mer</span>
        </button>
      </nav>
    </div>
    </SubscriptionGuard>
  );
};

export default WorkerLayout;
